import React, { useState } from 'react';
import { Link } from 'react-router';
import { promoteImportedLscMembers, LscApiError } from '../api';
import type { LscImportedMemberPromotionResult } from '../types';
import ErrorBanner from './ErrorBanner';
import { lscPath } from '../routing';

interface ImportedMembersPromotionPanelProps {
  leagueId: string;
  snapshotId?: string;
  onApplied?: () => void;
}

const ImportedMembersPromotionPanel: React.FC<ImportedMembersPromotionPanelProps> = ({
  leagueId,
  snapshotId,
  onApplied,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<LscImportedMemberPromotionResult | null>(null);
  const [result, setResult] = useState<LscImportedMemberPromotionResult | null>(null);

  const runPromotion = async (dryRun: boolean) => {
    setLoading(true);
    setError(null);
    if (dryRun) {
      setPreview(null);
      setResult(null);
    }
    try {
      const data = await promoteImportedLscMembers(leagueId, {
        source: 'neonsportz',
        snapshot_id: snapshotId,
        dry_run: dryRun,
      });
      if (dryRun) {
        setPreview(data);
      } else {
        setResult(data);
        onApplied?.();
      }
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not promote imported members.');
    } finally {
      setLoading(false);
    }
  };

  const items = preview?.items || [];

  return (
    <div className="card space-y-4" data-testid="imported-members-promotion-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Imported roster</h2>
        <p className="text-sm text-secondary-400">
          Create pending member slots from your latest NeonSportz import. This does not create user
          accounts, claims, or Twitch logins.
        </p>
      </div>

      <button
        type="button"
        className="btn-secondary"
        disabled={loading}
        onClick={() => void runPromotion(true)}
        data-testid="preview-pending-members-button"
      >
        {loading && !preview ? 'Loading preview...' : 'Preview pending members'}
      </button>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {preview && (
        <div className="space-y-3" data-testid="imported-members-preview">
          <p className="text-sm text-secondary-300">
            Participants found: {preview.summary?.participants_found ?? 0} · Would create:{' '}
            {preview.summary?.would_create ?? 0} · Would skip: {preview.summary?.would_skip ?? 0}
          </p>
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left text-secondary-300">
                <thead>
                  <tr className="text-secondary-400 border-b border-secondary-700">
                    <th className="py-2 pr-4">Team</th>
                    <th className="py-2 pr-4">Discord / NeonSportz member</th>
                    <th className="py-2 pr-4">Discord ID</th>
                    <th className="py-2 pr-4">In-game identity</th>
                    <th className="py-2 pr-4">Existing member match</th>
                    <th className="py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={`${item.side_label}-${index}`} className="border-b border-secondary-800">
                      <td className="py-2 pr-4">{item.side_label || '—'}</td>
                      <td className="py-2 pr-4">{item.discord_username || '—'}</td>
                      <td className="py-2 pr-4">{item.discord_id || 'Missing'}</td>
                      <td className="py-2 pr-4">{item.in_game_name || '—'}</td>
                      <td className="py-2 pr-4">{item.reason || '—'}</td>
                      <td className="py-2">{item.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button
            type="button"
            className="btn-primary"
            disabled={loading || (preview.summary?.would_create ?? 0) === 0}
            onClick={() => void runPromotion(false)}
            data-testid="create-pending-members-button"
          >
            {loading && preview ? 'Creating...' : 'Create pending members'}
          </button>
        </div>
      )}

      {result && (
        <div className="space-y-2" data-testid="imported-members-promotion-result">
          <p className="text-sm text-secondary-200">
            Created: {result.summary?.created ?? 0} · Updated: {result.summary?.updated ?? 0} ·
            Skipped: {result.summary?.skipped ?? 0}
          </p>
          <Link
            to={lscPath(`/leagues/${leagueId}/members`)}
            className="btn-secondary inline-block"
            data-testid="view-members-link"
          >
            View Members
          </Link>
        </div>
      )}
    </div>
  );
};

export default ImportedMembersPromotionPanel;
